import { HttpErrorResponse } from '@angular/common/http'
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'

import { OnInit } from '@angular/core'
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { GuardianAccessComponent } from '@se-po/se-my-sportsengine-feature-guardian-access'
import { AnalyticsService, HouseholdGuardianshipsService, HouseholdPersonasService } from '@se-po/shared-data-access-services'
import { catchError, defaultIfEmpty, forkJoin, Observable, of } from 'rxjs'
import { SeFeFormService } from 'se-fe-form-service'
import { SeFeToastService } from 'se-fe-toast'
import { CentralServiceResources as cs } from 'se-resource-types'
import { RelatedGuardiansResult, UserProfile } from 'se-resource-types/dist/lib/CentralService/Households'
import { NewPersonSubmitter } from '../new-person-submitter-service'
@Component({
  selector: 'se-po-add-guardian',
  templateUrl: './add-guardian.component.html',
  styleUrls: ['./add-guardian.component.scss'],
  standalone: false,
})

export class AddGuardianComponent implements OnInit {
  @Input() public profiles: UserProfile[] = []
  @Input() public guardians: RelatedGuardiansResult[] = []
  @Output() public guardianInvited: EventEmitter<void> = new EventEmitter()
  @ViewChild('guardianAccess') public guardianAccess: GuardianAccessComponent

  public error: string
  public form: UntypedFormGroup
  public allInvites: any[] = []

  constructor(
    private analyticsService: AnalyticsService,
    private formBuilder: FormBuilder,
    private formService: SeFeFormService,
    private householdGuardianshipsService: HouseholdGuardianshipsService,
    private householdPersonasService: HouseholdPersonasService,
    private newPersonService: NewPersonSubmitter,
    private toastService: SeFeToastService
  ) {
  }

  public ngOnInit(): void {
    this.getAllInvites()
    this.createForm()
  }

  public getAllInvites(): void {
    const inviteRequests = this.profiles.map(profile => this.householdPersonasService.findPersonaGuardianInvites(profile.persona.id))
    forkJoin(inviteRequests).subscribe(invites => {
      this.allInvites = invites.flat()
    })
  }

  public success(): void {
    this.toastService.success(`Invitation sent to ${this.form.value.email}`)
    this.guardianInvited.emit()
   }

  public createForm(): void {
    this.form = this.formBuilder.group(
      {
        email: ['', Validators.required]
      }
    )
  }

  public submit(): void {
    this.error = null
    const formValid = this.formService.isValid(this.form)
    const guardianAccessValid = this.guardianAccess.isValid()

    if (!formValid || !guardianAccessValid) {
      return
    }
    this.analyticsService.seEvent('Household.AddPerson.InviteGuardian')

    const { guardianships, guardianInvites } = this.separateExistingGuardianships()
    forkJoin([
      this.updateGuardianships(guardianships),
      this.createGuardianInvites(guardianInvites)
    ]).subscribe(() => !this.error && this.success())
  }

  public submitOne(personaId: number, access: string): Observable<cs.Households.PersonaGuardianInvite> {
    return this.newPersonService.createGuardian(
      access,
      this.form.value.email,
      personaId,
      'mySe'
    ).pipe(
      catchError((response: HttpErrorResponse) => {
        this.error = response.error.error.messages?.[0]
        return of(null)
      })
    )
  }

  private separateExistingGuardianships(): any {
    const existingGuardianship = this.guardians.find((g) => g.user.email_address.address === this.form.value.email)
    const profiles = existingGuardianship?.profiles?.map((p) => ({ personaId: p.persona_id, guardianshipId: p.guardianship_id }))
    return this.guardianAccess.value.reduce((acc, cur) => {
      const existingProfile = profiles?.find((id) => id.personaId === cur.id)
      if (existingProfile) acc.guardianships.push({ id: existingProfile.guardianshipId, access: cur.access })
      else acc.guardianInvites.push(cur)
      return acc
    }, { guardianships: [], guardianInvites: [] })
  }

  private updateGuardianships(guardianAccessValues: any[]): Observable<any> {
    return forkJoin(guardianAccessValues.map((item) =>
      this.householdGuardianshipsService.update(item.id, item.access).pipe(
        catchError(() => {
          this.error = 'Error updating access level'
          return of()
        })
      )
    )).pipe(defaultIfEmpty([]))
  }

  private createGuardianInvites(guardianAccessValues: any[]): Observable<any> {
    return forkJoin(guardianAccessValues.map(item => this.submitOne(item.id, item.access))).pipe(defaultIfEmpty([]))
  }

}
